import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Maximize2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

interface JsonViewerProps {
  value: unknown;
  /** Panel heading; also the expanded dialog's title. */
  title?: string;
  /** Depth expanded on first render (deeper nodes start collapsed). */
  initialDepth?: number;
  /** Height cap for the inline panel; the expand dialog is uncapped. */
  className?: string;
  /** Marks this text (case-insensitive) in keys and values — a search the caller applied. */
  highlight?: string;
  /** Root test id; controls and the expand dialog derive `{testId}-…` ids from it. */
  testId?: string;
}

/**
 * Read-only JSON panel for Temporal payloads (input, result, attributes, query
 * results): collapsible tree, expand-all / collapse-all, copy-as-JSON, and an
 * open-in-dialog view for large payloads. Values arrive already masked, so it
 * renders them verbatim.
 */
export function JsonViewer({
  value,
  title,
  initialDepth = 2,
  className,
  highlight,
  testId = 'workflow-inspector-json-viewer',
}: JsonViewerProps) {
  const { t } = useTranslation('workflow-inspection');
  const [expanded, setExpanded] = useState(false);
  // null = default depth; true/false = every node opened / closed. The tree is
  // re-keyed on change so each node re-reads its initial open state.
  const [allOpen, setAllOpen] = useState<boolean | null>(null);
  const text = JSON.stringify(value, null, 2);
  const treeDepth = allOpen === null ? initialDepth : allOpen ? Infinity : 1;

  return (
    <div className="bg-background relative rounded-lg border" data-testid={testId}>
      <div className="absolute top-1.5 right-1.5 z-10 flex items-center gap-0.5">
        <IconAction
          label={allOpen ? t('json.collapseAll') : t('json.expandAll')}
          testId={`${testId}-toggle-all-button`}
          onClick={() => {
            setAllOpen(!allOpen);
          }}
        >
          {allOpen ? (
            <ChevronsDownUp aria-hidden="true" className="size-3.5" />
          ) : (
            <ChevronsUpDown aria-hidden="true" className="size-3.5" />
          )}
        </IconAction>
        <CopyIconButton
          text={text}
          label={t('json.copy')}
          copyMessage={t('json.copied')}
          className="h-7 w-7"
          tooltip
        />
        <IconAction
          label={t('json.expand')}
          testId={`${testId}-expand-button`}
          onClick={() => {
            setExpanded(true);
          }}
        >
          <Maximize2 aria-hidden="true" className="size-3.5" />
        </IconAction>
      </div>
      <div className={cn('max-h-80 overflow-auto p-3 pr-28 font-mono text-xs', className)}>
        <JsonNode
          key={String(allOpen)}
          value={value}
          depth={0}
          initialDepth={treeDepth}
          highlight={highlight}
        />
      </div>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent
          className="max-h-[85vh] max-w-4xl overflow-hidden"
          data-testid={`${testId}-dialog`}
        >
          <DialogHeader>
            <DialogTitle>{title ?? t('json.expand')}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-auto font-mono text-xs">
            <JsonNode value={value} depth={0} initialDepth={Infinity} highlight={highlight} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Icon-only panel action with a styled tooltip (no native `title`). */
function IconAction({
  label,
  onClick,
  children,
  testId,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  /** `data-testid` for the button. */
  testId?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 cursor-pointer"
          aria-label={label}
          onClick={onClick}
          data-testid={testId}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** `text` with every case-insensitive occurrence of `needle` wrapped in a <mark>. */
function Marked({ text, needle }: { text: string; needle?: string | undefined }) {
  const q = needle?.trim().toLowerCase();
  if (!q) return <>{text}</>;
  const parts: ReactNode[] = [];
  const lower = text.toLowerCase();
  let at = 0;
  for (let hit = lower.indexOf(q); hit !== -1; hit = lower.indexOf(q, at)) {
    if (hit > at) parts.push(text.slice(at, hit));
    parts.push(
      <mark key={hit} className="bg-warning/30 rounded-sm text-inherit">
        {text.slice(hit, hit + q.length)}
      </mark>,
    );
    at = hit + q.length;
  }
  parts.push(text.slice(at));
  return <>{parts}</>;
}

/** One node of the tree — scalars inline, objects/arrays collapsible. */
function JsonNode({
  value,
  depth,
  initialDepth,
  label,
  highlight,
}: {
  value: unknown;
  depth: number;
  initialDepth: number;
  label?: string;
  highlight?: string | undefined;
}) {
  const { t } = useTranslation('workflow-inspection');
  const [open, setOpen] = useState(depth < initialDepth);
  const isArray = Array.isArray(value);
  const isObject = typeof value === 'object' && value !== null;

  const keyLabel =
    label !== undefined ? (
      <span className="text-primary">
        &quot;
        <Marked text={label} needle={highlight} />
        &quot;:{' '}
      </span>
    ) : null;

  if (!isObject) {
    return (
      <div className="leading-5 break-all whitespace-pre-wrap">
        {keyLabel}
        <JsonScalar value={value} highlight={highlight} />
      </div>
    );
  }

  const entries: [string, unknown][] = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v])
    : Object.entries(value as Record<string, unknown>);
  const [openBrace, closeBrace] = isArray ? ['[', ']'] : ['{', '}'];
  const countLabel = isArray
    ? t('json.items', { count: entries.length })
    : t('json.keys', { count: entries.length });

  if (entries.length === 0) {
    return (
      <div className="leading-5">
        {keyLabel}
        {openBrace}
        {closeBrace}
      </div>
    );
  }

  return (
    <div className="leading-5">
      <div className="flex items-start">
        <Button
          type="button"
          variant="ghost"
          className="text-muted-foreground -ml-1 h-5 w-4 shrink-0 cursor-pointer p-0"
          aria-label={open ? t('json.collapseNode') : t('json.expandNode')}
          aria-expanded={open}
          onClick={() => {
            setOpen(!open);
          }}
          data-testid="workflow-inspector-json-node-toggle"
        >
          {open ? (
            <ChevronDown aria-hidden="true" className="size-3" />
          ) : (
            <ChevronRight aria-hidden="true" className="size-3" />
          )}
        </Button>
        <span>
          {keyLabel}
          {openBrace}
          {!open && (
            <span className="text-muted-foreground">
              {' '}
              {countLabel} {closeBrace}
            </span>
          )}
        </span>
      </div>
      {open && (
        <>
          <div className="border-border/60 ml-1.5 border-l pl-3">
            {entries.map(([k, v]) => (
              <JsonNode
                key={k}
                value={v}
                depth={depth + 1}
                initialDepth={initialDepth}
                highlight={highlight}
                {...(isArray ? {} : { label: k })}
              />
            ))}
          </div>
          <div>{closeBrace}</div>
        </>
      )}
    </div>
  );
}

/** Scalar with JSON-style colouring (strings quoted, null/booleans/numbers distinct). */
function JsonScalar({ value, highlight }: { value: unknown; highlight?: string | undefined }) {
  if (value === null || value === undefined)
    return <span className="text-muted-foreground">null</span>;
  if (typeof value === 'string')
    return (
      <span className="text-success">
        <Marked text={JSON.stringify(value)} needle={highlight} />
      </span>
    );
  if (typeof value === 'number')
    return (
      <span className="text-warning">
        <Marked text={String(value)} needle={highlight} />
      </span>
    );
  if (typeof value === 'boolean')
    return (
      <span className="text-info">
        <Marked text={String(value)} needle={highlight} />
      </span>
    );
  return <span>{JSON.stringify(value)}</span>;
}
